import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SocialProvider } from "./context/SocialContext";
import { PageLoader } from "./components/shared";
import ErrorBoundary from "./components/ErrorBoundary";

// public
import Landing from "./pages/Landing";
import BecomeOrganizer from "./pages/BecomeOrganizer";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import LegalPage from "./pages/LegalPage";
import PublicTicket from "./pages/PublicTicket";
import CheckerPortal from "./pages/CheckerPortal";
import RsvpResponse from "./pages/RsvpResponse";

// customer
import CustomerLayout from "./components/CustomerLayout";
import Discovery from "./pages/customer/Discovery";
import EventDetail from "./pages/customer/EventDetail";
import MyBookings from "./pages/customer/MyBookings";
import BookingDetail from "./pages/customer/BookingDetail";
import Saved from "./pages/customer/Saved";
import Rewards from "./pages/customer/Rewards";
import PaymentMethods from "./pages/customer/PaymentMethods";
import AccountSettings from "./pages/customer/AccountSettings";
import AskAI from "./pages/customer/AskAI";
import Support from "./pages/customer/Support";

// organizer
import OrganizerLayout from "./pages/organizer/OrganizerLayout";
import OrgDashboard from "./pages/organizer/OrgDashboard";
import MyEvents from "./pages/organizer/MyEvents";
import EventEditor from "./pages/organizer/EventEditor";
import Analytics from "./pages/organizer/Analytics";
import Services from "./pages/organizer/Services";
import PackageBuilder from "./pages/organizer/PackageBuilder";
import Copilot from "./pages/organizer/Copilot";
import Settlements from "./pages/organizer/Settlements";
import Checkin from "./pages/organizer/Checkin";
import Onboarding from "./pages/organizer/Onboarding";
import AccessControl from "./pages/organizer/AccessControl";
import Coupons from "./pages/organizer/Coupons";
import Guests from "./pages/organizer/Guests";
import Wallet from "./pages/organizer/Wallet";
import Attendees from "./pages/organizer/Attendees";
import Tutorial from "./pages/organizer/Tutorial";

// admin
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrganizers from "./pages/admin/AdminOrganizers";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminServices from "./pages/admin/AdminServices";
import AdminCatalog from "./pages/admin/AdminCatalog";
import AdminConfig from "./pages/admin/AdminConfig";
import AdminContent from "./pages/admin/AdminContent";
import AdminFinance from "./pages/admin/AdminFinance";
import AdminEmails from "./pages/admin/AdminEmails";
import AdminSupport from "./pages/admin/AdminSupport";
import AdminChat from "./pages/admin/AdminChat";
import AdminTutorial from "./pages/admin/AdminTutorial";

const homeFor = (role) => (role === "admin" ? "/admin" : role === "organizer" ? "/organizer" : "/discover");

function RoleRoute({ role, children }) {
  const { user, loading } = useAuth();
  const hasToken = !!localStorage.getItem("eventra_token");
  if (loading || (!user && hasToken)) return <div className="min-h-screen grid place-items-center"><PageLoader /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/become-organizer" element={<BecomeOrganizer />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/auth/google" element={<AuthCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/events/:id" element={<EventDetail />} />
      <Route path="/t/:token" element={<PublicTicket />} />
      <Route path="/rsvp/:token" element={<RsvpResponse />} />
      <Route path="/scan" element={<CheckerPortal />} />

      {/* customer */}
      <Route element={<RoleRoute role="customer"><CustomerLayout /></RoleRoute>}>
        <Route path="/discover" element={<Discovery />} />
        <Route path="/ask-ai" element={<AskAI />} />
        <Route path="/bookings" element={<MyBookings />} />
        <Route path="/bookings/:id" element={<BookingDetail />} />
        <Route path="/saved" element={<Saved />} />
        <Route path="/rewards" element={<Rewards />} />
        <Route path="/payment-methods" element={<PaymentMethods />} />
        <Route path="/settings" element={<AccountSettings />} />
        <Route path="/support" element={<Support />} />
      </Route>

      {/* organizer */}
      <Route path="/organizer" element={<RoleRoute role="organizer"><OrganizerLayout /></RoleRoute>}>
        <Route index element={<OrgDashboard />} />
        <Route path="events" element={<MyEvents />} />
        <Route path="events/new" element={<EventEditor />} />
        <Route path="events/:id/edit" element={<EventEditor />} />
        <Route path="coupons" element={<Coupons />} />
        <Route path="guests" element={<Guests />} />
        <Route path="attendees" element={<Attendees />} />
        <Route path="tutorial" element={<Tutorial />} />
        <Route path="wallet" element={<Wallet />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="services" element={<Services />} />
        <Route path="package-builder" element={<PackageBuilder />} />
        <Route path="copilot" element={<Copilot />} />
        <Route path="settlements" element={<Settlements />} />
        <Route path="checkin" element={<Checkin />} />
        <Route path="access" element={<AccessControl />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="support" element={<Support />} />
      </Route>

      {/* admin */}
      <Route path="/admin" element={<RoleRoute role="admin"><AdminLayout /></RoleRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="organizers" element={<AdminOrganizers />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="events" element={<AdminEvents />} />
        <Route path="services" element={<AdminServices />} />
        <Route path="catalog" element={<AdminCatalog />} />
        <Route path="config" element={<AdminConfig />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="finance" element={<AdminFinance />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="chat" element={<AdminChat />} />
        <Route path="tutorial" element={<AdminTutorial />} />
        <Route path="support" element={<AdminSupport />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <SocialProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <AppRouter />
          </ErrorBoundary>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </SocialProvider>
    </AuthProvider>
  );
}
